import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { useFuelEconomyByTimeOfDay } from "../hooks/useFuelEconomyHooks";
import type { FuelEconomyHookParams } from "../hooks/useFuelEconomyHooks";
import { MultiSeriesLineChart } from "../common/MultiSeriesLineChart";
import "./FuelEconomyReport.css";

type Props = FuelEconomyHookParams;

export const FuelConsumptionWidget: React.FC<Props> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const { data, loading: isLoading, error } = useFuelEconomyByTimeOfDay({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const hasData = useMemo(() => {
    if (!data) return false;
    const morning = data.morningValues?.length ?? 0;
    const noon = data.noonValues?.length ?? 0;
    const afternoon = data.afternoonValues?.length ?? 0;
    const night = data.nightValues?.length ?? 0;
    return morning > 0 || noon > 0 || afternoon > 0 || night > 0;
  }, [data]);

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("fuelEconomyReport.fuelConsumption", {
            _: "Fuel consumption",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : data ? (
          <MultiSeriesLineChart
            data={data}
            fromDate={fromDate}
            toDate={toDate}
            periodType={periodType}
          />
        ) : null}
      </CardContent>
    </Card>
  );
};

export default FuelConsumptionWidget;
